/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./FilterBaseController',
	'./RangeFilterView',
	'./RangeFilterModel',
	'underscore'
], function (BaseClass, FilterView, FilterModel, _) {
	'use strict';


	/**
	 * The controller holds the business logic for the range filter
	 */
	var RangeFilterController = BaseClass.extend({
		MIN_HEIGHT: 275, // Min height of common control
		HEIGHT: 350, // Height of common control

		init: function () {
			RangeFilterController.inherited('init', this, arguments);
			this.id = _.uniqueId('RangeFilterController');
			this.itemContext.eventSourceId = this.id;
			this.ready = Promise.resolve(null);
		},
		initialize: function () {
			this._initializeExtraData();
			this._createFilterModel();
			this._createFilterView();
			this.setLoading();
			this._loadMinMax().then(this.onInitializationDone.bind(this));
			return Promise.resolve();
		},

		_initializeExtraData: function () {
			this._initializePageContext(/*isRange*/true);
		},

		/**
		 * Common Control Callback handler - invoked when the following value changes.
		 * - fromValue, toValue, and inverted
		 * - also filterSelectionType which we don't support for now.
		 */
		onChange: function (payload) {
			var inverted = payload.inverted, from = payload.fromValue, to = payload.toValue;

			var globalItem = this._getGlobalPageContextItem();
			if (globalItem) {
				//Invert state of the one on global is forced to the one on tab
				inverted = globalItem.getInvert();

				if (globalItem.getInvert()) {
					// Ensure values are with the limit
					if (from > globalItem.getFrom()) {
						from = globalItem.getFrom();
					}
					if (to < globalItem.getTo()) {
						to = globalItem.getTo();
					}
				}
			}

			this.model.update(from, to, inverted);
		},

		/**
		 * Common Control Callback handler - update PageContext with current filter model
		 */
		onDone: function () {
			var state = this.model.toJSON();
			if (state.min !== state.from || state.max !== state.to || state.inverted) {
				this.pageContextApi.updateFilterContext(this.itemContext, {
					command: 'update',
					condition: { from: state.from, to: state.to, invert: state.inverted }
				});
			} else {
				this.pageContextApi.updateFilterContext(this.itemContext, { command: 'clearValues' });
			}

			if (!this.view.isCompact) {
				this.view.remove();
			}
		},

		/**
		 * Common Control Callback handler
		 */
		onClear: function () {
			this.model.clearValues();
		},

		onSearch: function () {
		},

		onOpen: function () {
			this.setLoading();
			if (this._ensureFilterViewExists()) {
				// New view or model is created.
				this.view.renderViewComposition()
					.then(this._loadMinMax.bind(this))
					.then(this.removeLoading.bind(this));
			} else {
				this._refreshModel(this._getCurrentMinMaxArray());
				this.removeLoading();
			}
		},

		render: function () {
			this._refreshModel(this._getCurrentMinMaxArray());
			return this.view.renderViewComposition()
				.catch(this.logger.error.bind(this.logger));
		},

		/**
		 * This function is called by processContextChange if page context change impacts current filter.
		 * @return {Promise}
		 */
		_updateFilterDataOnPageContextChange: function () {
			return this._loadMinMax();
		},

		_loadMinMax: function () {
			return this.queryHelper.getMinMax()
				.then(function (minMaxArray) {
					this._refreshModel(minMaxArray);
				}.bind(this))
				.catch(function (e) {
					this.processError(e);
				}.bind(this));
		},

		_createFilterModel: function () {
			var modelOptions = {
				name: this.itemContext.itemName,
				logger: this.logger
			};
			var pageContextItem = this.getCurrentPageContextItem();
			if (pageContextItem) {
				modelOptions.name = pageContextItem.getItemName();
			}
			this.model = new FilterModel(modelOptions);
		},

		_createFilterView: function () {
			this.viewConfig = _.extend(this.viewConfig, {
				model: this.model,
				cbOnChange: this.onChange.bind(this),
				cbOnClear: this.onClear.bind(this),
				cbOnDone: this.onDone.bind(this),
				cbOnApply: this.onApply.bind(this),
				cbOnClose: this.onClose.bind(this),
				cbOnSearch: this.onSearch.bind(this),
				cbGetInvertDisabled: this._cbGetInvertDisabled.bind(this)
			});

			if ((this.itemContext.dataType === 'integer' && this.itemContext.aggregationType !== 'avg') ||
				(this.itemContext.aggregationType === 'countdistinct') || (this.itemContext.aggregationType === 'count')) {
				this.viewConfig.decimalPlaces = 0;
			} else {
				this.viewConfig.decimalPlaces = this._getMaximumFractionDigits();
			}
			this.view = new FilterView(this.viewConfig);
		},

		/** If there's a global exclude-type range filter, the tab range control itself would need
		 * to be split into two sliders to support invert.
		 * If there's a global include-type range filter, the tab range falls within that range,
		 * inverting should create multiple sub-ranges from global-min to tab-min and tab-max to global-max which is not supported.
		 * Until that is supported in the UI and in DSS, we need to disable the invert selection.
		 */
		_cbGetInvertDisabled: function () {
			return this._getGlobalPageContextItem();
		},

		_getMaximumFractionDigits: function () {
			var maximumFractionDigits = 3; // arbitrary default value 3.
			var pageContextItem = this.getCurrentPageContextItem();
			if (pageContextItem) {
				var info = pageContextItem.getMetadataInfo();
				if (info && info.maximumFractionDigits !== undefined) {
					maximumFractionDigits = info.maximumFractionDigits;
				}
			}
			return maximumFractionDigits;
		},

		_getCurrentMinMaxArray: function () {
			var result = [];
			var state = this.model.toJSON();
			result.push(state.min);
			result.push(state.max);
			return result;
		},

		_refreshModel: function (minMaxArray) {
			if (!this.model) {
				return;
			}

			var pageContextItem = this.getCurrentPageContextItem();
			var globalItem = this._getGlobalPageContextItem();
			var min, max, from, to, inverted;

			if (pageContextItem) {
				min = Number(pageContextItem.getRangeMinBound(minMaxArray[0]));
				max = Number(pageContextItem.getRangeMaxBound(minMaxArray[1]));
				inverted = pageContextItem.getInvert();

				if (pageContextItem.getFrom() !== '' || pageContextItem.getTo() !== '') {
					from = Number(pageContextItem.getFrom());
					to = Number(pageContextItem.getTo());
				} else {
					from = min;
					to = max;
				}
			} else {
				min = minMaxArray[0];
				max = minMaxArray[1];
				inverted = false;
				from = min;
				to = max;
			}

			if (globalItem && globalItem.getInvert()) {
				inverted = globalItem.getInvert();
			}

			this.model.reset(min, max, from, to, inverted);
		},

		refreshDataAndView: function () {
			return this._loadMinMax().then(function () {
				return this.view.renderViewComposition();
			}.bind(this));
		}
	});

	return RangeFilterController;
});
